import Phaser from 'phaser';
import type { AudioManager } from '@/game/systems/AudioManager';
import { RhythmEngine } from '@/game/systems/RhythmEngine';
import { SONGS } from '@/data/songs';
import type { ChartNote, Lane } from '@/types';
import { FOODS } from '@/data/foods';
import { INTERNAL_HEIGHT, INTERNAL_WIDTH } from '@/game/config/GameConfig';
import { PALETTE, bodyTextStyle, createButton, titleTextStyle } from '@/game/ui/theme';

const TOP_BAR_H = 150;
const BOTTOM_BAR_H = 90;
const GRID_TOP = TOP_BAR_H;
const GRID_HEIGHT = INTERNAL_HEIGHT - TOP_BAR_H - BOTTOM_BAR_H;

/**
 * A minimal but genuinely usable chart-authoring tool, reached via `?editor=true`.
 * The vertical axis of the grid is song time (top = 0s, bottom = song duration) and
 * the horizontal axis is lane + normalized X within that lane -- clicking anywhere
 * places a note with all three coordinates (time, lane, x) in a single action.
 * Notes are exported as plain ChartNote[] JSON, identical in shape to the
 * hand-authored/generated charts already used by real songs.
 */
export class ChartEditorScene extends Phaser.Scene {
  private audioManager!: AudioManager;
  private rhythmEngine!: RhythmEngine;
  private notes: ChartNote[] = [];
  private noteDots: Phaser.GameObjects.Arc[] = [];
  private playhead!: Phaser.GameObjects.Rectangle;
  private timeText!: Phaser.GameObjects.Text;
  private noteCountText!: Phaser.GameObjects.Text;
  private exportBox!: Phaser.GameObjects.Text;
  private currentFoodIndex = 0;
  private songIndex = 0;

  constructor() {
    super('ChartEditorScene');
  }

  create(): void {
    this.audioManager = this.game.registry.get('audioManager');
    this.add.rectangle(0, 0, INTERNAL_WIDTH, INTERNAL_HEIGHT, 0x0d0818).setOrigin(0);

    this.add.text(INTERNAL_WIDTH / 2, 16, 'Chart Editor', titleTextStyle(28)).setOrigin(0.5, 0);

    const backBtn = createButton(this, 80, 20, 120, 44, 'Back', PALETTE.panelLight, 18);
    backBtn.on('pointerup', () => {
      this.rhythmEngine?.stop();
      this.scene.start('HomeScene');
    });

    const playBtn = createButton(this, INTERNAL_WIDTH - 90, 20, 140, 44, 'Play/Pause', PALETTE.secondary, 16);
    playBtn.on('pointerup', () => this.togglePlay());

    const exportBtn = createButton(this, INTERNAL_WIDTH / 2, INTERNAL_HEIGHT - 44, 240, 60, 'Export JSON', PALETTE.primary, 22);
    exportBtn.on('pointerup', () => this.exportChart());

    this.timeText = this.add.text(20, 60, '', bodyTextStyle(18, '#cbb9e8'));
    this.noteCountText = this.add.text(INTERNAL_WIDTH - 20, 60, '', bodyTextStyle(18, '#cbb9e8')).setOrigin(1, 0);
    this.exportBox = this.add
      .text(20, INTERNAL_HEIGHT - BOTTOM_BAR_H - 26, '', { ...bodyTextStyle(12, '#7ee787'), wordWrap: { width: INTERNAL_WIDTH - 40 } })
      .setOrigin(0, 1);

    this.add.text(20, 100, "Click grid to place a note (top=0s). [F]=food type  [Del]=remove nearest  [Space]=play/pause", bodyTextStyle(14, '#e9d9ff'));

    // Grid background split into left/right lane halves.
    const gridBg = this.add.graphics();
    gridBg.fillStyle(PALETTE.panel, 0.5);
    gridBg.fillRect(0, GRID_TOP, INTERNAL_WIDTH / 2, GRID_HEIGHT);
    gridBg.fillStyle(PALETTE.panelLight, 0.5);
    gridBg.fillRect(INTERNAL_WIDTH / 2, GRID_TOP, INTERNAL_WIDTH / 2, GRID_HEIGHT);
    gridBg.lineStyle(2, 0xffffff, 0.15);
    for (let i = 1; i < 10; i++) {
      const y = GRID_TOP + (GRID_HEIGHT * i) / 10;
      gridBg.lineBetween(0, y, INTERNAL_WIDTH, y);
    }

    this.playhead = this.add.rectangle(INTERNAL_WIDTH / 2, GRID_TOP, INTERNAL_WIDTH, 3, 0x00ff88, 0.9).setOrigin(0.5, 0);

    const hitZone = this.add.zone(0, GRID_TOP, INTERNAL_WIDTH, GRID_HEIGHT).setOrigin(0).setInteractive({ useHandCursor: true });
    hitZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => this.placeNoteAt(pointer.x, pointer.y));

    this.input.keyboard?.on('keydown-SPACE', () => this.togglePlay());
    this.input.keyboard?.on('keydown-F', () => {
      this.currentFoodIndex = (this.currentFoodIndex + 1) % FOODS.length;
    });
    this.input.keyboard?.on('keydown-DELETE', () => this.removeNearestNote());
    this.input.keyboard?.on('keydown-BACKSPACE', () => this.removeNearestNote());

    void this.loadSong(this.songIndex);
  }

  private async loadSong(index: number): Promise<void> {
    const song = SONGS[index];
    const buffer = await this.audioManager.loadSong(song);
    this.rhythmEngine = new RhythmEngine(this.audioManager, song, song.chart, buffer);
    this.notes = song.chart.notes.map((n) => ({ ...n }));
    this.redrawNotes();
  }

  private togglePlay(): void {
    if (!this.rhythmEngine) return;
    if (this.rhythmEngine.isPlaying()) {
      this.rhythmEngine.pause();
    } else if (this.rhythmEngine.getState() === 'paused') {
      this.rhythmEngine.resume();
    } else {
      this.rhythmEngine.start(0);
    }
  }

  private placeNoteAt(x: number, y: number): void {
    if (!this.rhythmEngine || y < GRID_TOP) return;
    const lane: Lane = x < INTERNAL_WIDTH / 2 ? 'left' : 'right';
    const laneOffset = lane === 'left' ? 0 : INTERNAL_WIDTH / 2;
    const normX = Phaser.Math.Clamp((x - laneOffset) / (INTERNAL_WIDTH / 2), 0, 1);
    const time = ((y - GRID_TOP) / GRID_HEIGHT) * this.rhythmEngine.duration;
    const food = FOODS[this.currentFoodIndex];

    const note: ChartNote = { time: Math.round(time * 1000) / 1000, lane, x: Math.round(normX * 100) / 100, type: food.id, pitch: 72 };
    this.notes.push(note);
    this.notes.sort((a, b) => a.time - b.time);
    this.redrawNotes();
  }

  private removeNearestNote(): void {
    const pointer = this.input.activePointer;
    if (!this.rhythmEngine || this.notes.length === 0) return;
    let bestIndex = -1;
    let bestDist = Infinity;
    this.notes.forEach((note, i) => {
      const lane: Lane = note.lane;
      const laneOffset = lane === 'left' ? 0 : INTERNAL_WIDTH / 2;
      const x = laneOffset + note.x * (INTERNAL_WIDTH / 2);
      const y = GRID_TOP + (note.time / this.rhythmEngine.duration) * GRID_HEIGHT;
      const dist = Phaser.Math.Distance.Between(pointer.x, pointer.y, x, y);
      if (dist < bestDist) {
        bestDist = dist;
        bestIndex = i;
      }
    });
    if (bestIndex >= 0 && bestDist < 40) {
      this.notes.splice(bestIndex, 1);
      this.redrawNotes();
    }
  }

  private redrawNotes(): void {
    this.noteDots.forEach((d) => d.destroy());
    this.noteDots = [];
    if (!this.rhythmEngine) return;
    for (const note of this.notes) {
      const laneOffset = note.lane === 'left' ? 0 : INTERNAL_WIDTH / 2;
      const x = laneOffset + note.x * (INTERNAL_WIDTH / 2);
      const y = GRID_TOP + (note.time / this.rhythmEngine.duration) * GRID_HEIGHT;
      const food = FOODS.find((f) => f.id === note.type) ?? FOODS[0];
      const dot = this.add.circle(x, y, 7, food.color).setStrokeStyle(2, 0xffffff, 0.8);
      this.noteDots.push(dot);
    }
    this.noteCountText.setText(`${this.notes.length} notes | food: ${FOODS[this.currentFoodIndex].name}`);
  }

  private exportChart(): void {
    const json = JSON.stringify({ notes: this.notes }, null, 2);
    this.exportBox.setText(json.slice(0, 900));

    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${SONGS[this.songIndex].id}-chart.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  update(): void {
    if (!this.rhythmEngine) return;
    const t = this.rhythmEngine.currentSongTime;
    this.playhead.y = GRID_TOP + (t / this.rhythmEngine.duration) * GRID_HEIGHT;
    this.timeText.setText(
      `time: ${t.toFixed(2)}s  beat: ${this.rhythmEngine.currentBeat.toFixed(1)}  bpm: ${this.rhythmEngine.bpm}  state: ${this.rhythmEngine.getState()}`
    );
  }
}
